/**
 * translations.js — all user-visible strings for en / de
 *
 * Preset and theme names are intentionally left as-is (international names).
 * Phase identifiers used as internal keys ('Work', 'Short Break', etc.) are
 * kept in English in state; only their display labels are translated.
 */

const translations = {
  en: {
    // ── Timer UI ─────────────────────────────────────────────────────────────
    start: 'Start',
    pause: 'Pause',
    reset: 'Reset',
    quickAdjust: 'Quick adjust',
    quickActions: 'Quick actions',
    openSettingsAction: 'Open settings',
    enterMinimalPlayerAction: 'Enter minimalist player',
    exitMinimalPlayerAction: 'Exit minimalist player',
    skipPhase: 'Skip phase',
    endBreakNow: 'End break now',
    pomodoroProgressAria: (remaining, total) => `${remaining} of ${total} pomodoro${total !== 1 ? 's' : ''} remaining until long break`,
    shortcutHint: 'Shortcuts: {space} start/pause · {r} reset · {s} settings · {f} focus · {h} hints',
    phaseEndsAt: 'Ends at',
    sessionEndsAt: 'Session ends at',
    presetLabel: 'Preset:',
    themeLabel: 'Theme:',

    // ── Phase headings ───────────────────────────────────────────────────────
    phaseCountdown: 'Get ready!',
    phaseWork: 'Work',
    phaseWorkTodayPrefix: "Today's session",
    phaseShortBreak: 'Short Break',
    phaseLongBreak: 'Long Break',
    phasePomodoroFlow: 'Pomodoro Flow',

    // ── Break guidance ───────────────────────────────────────────────────────
    kickerShortBreak: 'Short break suggestion',
    kickerLongBreak: 'Long break suggestion',
    breakEncouragementShort: 'Keep it light. The point is to come back clearer, not to start a side quest.',
    breakEncouragementLong: 'Take the full exhale. A longer pause is part of the system, not weakness.',
    breakMoments: [
      {
        label: 'Stretch',
        title: 'Unknot the posture a bit.',
        body: 'Roll your shoulders, unclench your jaw, and stand up for ten slow seconds.',
      },
      {
        label: 'Breathe',
        title: 'Let the sprint end cleanly.',
        body: 'Take one slower breath in, one slower breath out, and let your eyes leave the screen.',
      },
      {
        label: 'Hydrate',
        title: 'Take a sip before the next round.',
        body: 'A little water now is better than realizing you were a desert twenty minutes later.',
      },
      {
        label: 'Reset',
        title: 'Check your next intention.',
        body: 'Ask what the very next useful move is, then leave the answer simple.',
      },
    ],

    // ── Ad slot ──────────────────────────────────────────────────────────────
    adLabel: 'Ad',
    adCloseAria: 'Close ad',

    // ── Header ───────────────────────────────────────────────────────────────
    headerSettings: 'Settings',

    // ── Settings panel ───────────────────────────────────────────────────────
    settingsSectionTimer: 'Timer',
    settingsSectionTimerCopy: 'Set your core work and break rhythm without digging through the whole panel.',
    settingsCategoryNavLabel: 'Settings categories',
    settingsCategoryTimerTabCopy: 'Durations and presets',
    settingsSectionBehavior: 'Behavior',
    settingsSectionBehaviorCopy: 'Decide how much the timer should automate and how much manual control you want nearby.',
    settingsCategoryBehaviorTabCopy: 'Automation and controls',
    settingsSectionAppearance: 'Appearance',
    settingsSectionAppearanceCopy: 'Keep the panel easy on the eyes and leave focus cues visible when they help.',
    settingsCategoryAppearanceTabCopy: 'Themes and hints',
    settingsSectionAlerts: 'Alerts',
    settingsSectionAlertsCopy: 'Tune how the app should get your attention when a phase changes.',
    settingsCategoryAlertsTabCopy: 'Sound and notifications',
    settingsSectionTheme: 'Theme',
    settingsActiveTheme: 'Active theme:',
    settingsSectionPresets: 'Presets',
    settingsActivePreset: 'Active preset:',
    settingsSectionTime: 'Time (minutes)',
    settingsLabelPomodoro: 'Pomodoro',
    settingsLabelShortBreak: 'Short Break',
    settingsLabelLongBreak: 'Long Break',
    settingsLabelIntervals: 'Intervals',
    settingsSectionSignal: 'Signal sound',
    settingsLabelSignalVolume: 'Signal volume',
    settingsSectionFeatures: 'Features',
    settingsLabelCountdown: 'Enable 10s Countdown',
    settingsLabelAutoStart: 'Auto Start Pomodoros',
    settingsLabelSkipControls: 'Enable skip / end-break controls',
    settingsLabelExtensionControls: 'Enable quick time extension controls',
    settingsLabelShowShortcutHints: 'Show keyboard shortcut hints',
    settingsBtnApply: 'Apply',
    settingsBtnClose: 'Close',
    settingsBtnResetDefaults: 'Reset to defaults',
    settingsBtnTestSound: 'Test sound',


    // ── Preset descriptions (keyed by preset id) ─────────────────────────────
    presetDescriptions: {
      classic: '25 / 5 · 4 rounds',
      'deep-work': '50 / 10 · 3 rounds',
      sprint: '15 / 3 · 4 rounds',
      study: '45 / 15 · 3 rounds',
      custom: 'Manual durations',
    },

    // ── Theme descriptions (keyed by theme id) ────────────────────────────────
    themeDescriptions: {
      aurora: 'Cool sky blues with calm mint accents',
      ember: 'Warm sandstone and ember reds for a richer desk mood',
      midnight: 'Dark slate tones with luminous cyan highlights',
      sage: 'Muted eucalyptus greens with clean stone neutrals',
      dusk: 'Soft violet dusk tones with brighter orchid accents',
      graphite: 'Refined charcoal surfaces with crisp electric blue cues',
    },

    // ── Home screen ──────────────────────────────────────────────────────────
    homeAriaLabel: 'Pomodoro Flow site information',
    homeEyebrow: 'Focus timer for deep work',
    homeHero: 'Pomodoro Flow helps you work in deliberate intervals instead of drifting.',
    homeLead:
      'Use the timer for study sessions, writing, coding, admin work, or any task that benefits from a clear rhythm. The site is designed to stay simple: start the timer, adjust your settings, and get back to the work.',
    homeHighlights: [
      'Free browser-based Pomodoro timer with no account required.',
      'Customizable work, short-break, long-break, countdown, and interval settings.',
      'Built-in presets help you start quickly without overthinking the setup.',
    ],
    homeFeatureIntroTitle: 'What Pomodoro Flow helps you do',
    homeFeatureIntroLead:
      'The timer stays lean, but it covers the practical pieces most people need to turn a vague task list into a repeatable work rhythm.',
    homeWorkflowLabel: 'Workflow fit:',
    homeFeatureCards: [
      {
        title: 'Shape the work block around the task',
        body: 'Adjust focus, short-break, long-break, and interval lengths so the timer matches the job instead of forcing every session into the same mold.',
        points: [
          'Use shorter rounds for admin, email, or revision drills.',
          'Use longer focus blocks for writing, coding, or exam prep.',
        ],
        workflow: 'A student can switch to a longer study preset for revision, while a knowledge worker can shorten cycles for inbox clearing or meeting follow-up.',
      },
      {
        title: 'Keep momentum without fiddling',
        body: 'Start, pause, reset, countdown, and optional auto-start behavior make it easy to begin quickly and keep the session moving.',
        points: [
          'Countdown gives you a short runway before focus starts.',
          'Pause and reset stay available when real life interrupts the plan.',
        ],
        workflow: 'Useful when you want a clean start ritual, but still need room for interruptions, calls, or context switches during the day.',
      },
      {
        title: 'Build a repeatable rhythm instead of guessing when to stop',
        body: 'Short and long breaks are built into the flow, so recovery is part of the system rather than something you remember only after you are already cooked.',
        points: [
          'The timer cycles through work and recovery automatically.',
          'Long-break structure helps prevent endless grinding.',
        ],
        workflow: 'Good for deep work sessions where consistency matters more than brute force, especially across several rounds of concentration.',
      },
      {
        title: 'Adapt the interface to how you actually focus',
        body: 'Presets, theme choices, shortcut hints, manual controls, and focus-supporting options help the timer fit different routines without turning the app into clutter.',
        points: [
          'Presets reduce setup friction when you just need to begin.',
          'Keyboard and focus features support faster, lower-friction control.',
        ],
        workflow: 'A regular user can keep a preferred setup for daily deep work, then still tweak the timer when the task or energy level changes.',
      },
    ],
    homeHowTitle: 'How the timer works',
    homeHowSteps: [
      'Set your preferred work and break lengths in Settings.',
      'Start the timer and focus on one task until the work session ends.',
      'Follow the short and long break rhythm to keep effort sustainable.',
      'Optionally enable countdown and auto-start behavior to match your routine.',
    ],
    homePomodoroTitle: 'What the Pomodoro technique is and why it works',
    homePomodoroLead:
      'The Pomodoro technique is a simple focus method: you work for a defined stretch of time, take a short break, and repeat. After several rounds, you take a longer break so the rhythm stays sustainable instead of turning into a grind.',
    homePomodoroHowItWorksTitle: 'How it works in practice',
    homePomodoroHowItWorksSteps: [
      'Pick one concrete task and work on it for a focused interval, traditionally around 25 minutes.',
      'When the timer ends, step away for a short break instead of immediately rolling into the next thing.',
      'Repeat the cycle a few times, then take a longer break to reset before starting again.',
    ],
    homePomodoroWhyItHelpsTitle: 'Why it helps people focus',
    homePomodoroBenefits: [
      'Smaller work sessions lower the resistance to starting, which helps when a task feels vague or heavy.',
      'Clear time boundaries reduce procrastination and wandering attention because there is a defined finish line.',
      'Planned breaks help you keep energy and mental freshness instead of noticing too late that your concentration is cooked.',
      'A repeatable work-break rhythm makes consistency easier, so progress depends less on mood or perfect motivation.',
    ],
    homeFaqTitle: 'Frequently asked questions',
    homeFaq: [
      {
        question: 'What data does the site store?',
        answer:
          'Only timer preferences such as durations and interval settings are stored locally in your browser. Privacy details are documented on the privacy page.',
      },
      {
        question: 'Can I use the site without marketing cookies?',
        answer:
          'Yes. The timer works fully without marketing consent. When consent is not given, Google automatically serves non-personalised ads (no advertising cookies, no tracking). You can manage or withdraw consent at any time via the privacy page.',
      },
      {
        question: 'Can I adjust the timer to match my own routine?',
        answer:
          'Yes. You can use a preset as a starting point or fine-tune work length, breaks, countdown, and interval behavior in Settings.',
      },
    ],
  },

  de: {
    // ── Timer UI ─────────────────────────────────────────────────────────────
    start: 'Start',
    pause: 'Pause',
    reset: 'Zurücksetzen',
    quickAdjust: 'Schnell anpassen',
    quickActions: 'Schnellaktionen',
    openSettingsAction: 'Einstellungen öffnen',
    enterMinimalPlayerAction: 'Minimalplayer starten',
    exitMinimalPlayerAction: 'Minimalplayer beenden',
    skipPhase: 'Phase überspringen',
    endBreakNow: 'Pause jetzt beenden',
    pomodoroProgressAria: (remaining, total) => `${remaining} von ${total} Pomodoro${total !== 1 ? 's' : ''} bis zur langen Pause`,
    shortcutHint: 'Tastenkürzel: {space} Start/Pause · {r} Zurücksetzen · {s} Einstellungen · {f} Fokus · {h} Hinweise',
    phaseEndsAt: 'Endet um',
    sessionEndsAt: 'Sitzung endet um',
    presetLabel: 'Vorlage:',
    themeLabel: 'Design:',

    // ── Phase headings ───────────────────────────────────────────────────────
    phaseCountdown: "Gleich geht's los!",
    phaseWork: 'Arbeitsphase',
    phaseWorkTodayPrefix: 'Heutiger Block',
    phaseShortBreak: 'Kurze Pause',
    phaseLongBreak: 'Lange Pause',
    phasePomodoroFlow: 'Pomodoro Flow',

    // ── Break guidance ───────────────────────────────────────────────────────
    kickerShortBreak: 'Vorschlag für die kurze Pause',
    kickerLongBreak: 'Vorschlag für die lange Pause',
    breakEncouragementShort: 'Kurz durchatmen. Ziel ist es, klarer zurückzukommen – kein neues Projekt starten.',
    breakEncouragementLong: 'Lass die Pause wirklich ankommen. Eine längere Unterbrechung ist Teil des Systems, keine Schwäche.',
    breakMoments: [
      {
        label: 'Dehnen',
        title: 'Kurz die Körperspannung lösen.',
        body: 'Schultern kreisen, Kiefer lockern, für zehn ruhige Sekunden aufstehen.',
      },
      {
        label: 'Atmen',
        title: 'Den Sprint sauber beenden.',
        body: 'Einmal tief einatmen, einmal tief ausatmen – und den Blick vom Bildschirm nehmen.',
      },
      {
        label: 'Trinken',
        title: 'Kurz was trinken vor der nächsten Runde.',
        body: 'Ein Schluck Wasser jetzt ist besser als zwanzig Minuten später zu merken, dass man dehydriert war.',
      },
      {
        label: 'Fokus setzen',
        title: 'Den nächsten Schritt klären.',
        body: 'Was ist der konkret nächste sinnvolle Schritt? Einfach halten.',
      },
    ],

    // ── Ad slot ──────────────────────────────────────────────────────────────
    adLabel: 'Anzeige',
    adCloseAria: 'Werbung schließen',

    // ── Header ───────────────────────────────────────────────────────────────
    headerSettings: 'Einstellungen',

    // ── Settings panel ───────────────────────────────────────────────────────
    settingsSectionTimer: 'Timer',
    settingsSectionTimerCopy: 'Lege den Kernrhythmus aus Arbeits- und Pausenzeiten fest, ohne dich durch alles wühlen zu müssen.',
    settingsCategoryNavLabel: 'Einstellungskategorien',
    settingsCategoryTimerTabCopy: 'Zeiten und Vorlagen',
    settingsSectionBehavior: 'Verhalten',
    settingsSectionBehaviorCopy: 'Bestimme, wie viel der Timer automatisch übernehmen soll und welche Sofortsteuerung sichtbar bleibt.',
    settingsCategoryBehaviorTabCopy: 'Automatik und Steuerung',
    settingsSectionAppearance: 'Darstellung',
    settingsSectionAppearanceCopy: 'Halte die Oberfläche klar und lass Fokus-Hinweise nur dann sichtbar, wenn sie dir helfen.',
    settingsCategoryAppearanceTabCopy: 'Designs und Hinweise',
    settingsSectionAlerts: 'Hinweise',
    settingsSectionAlertsCopy: 'Stelle ein, wie die App dich bei Phasenwechseln aufmerksam machen soll.',
    settingsCategoryAlertsTabCopy: 'Ton und Benachrichtigungen',
    settingsSectionTheme: 'Design',
    settingsActiveTheme: 'Aktives Design:',
    settingsSectionPresets: 'Vorlagen',
    settingsActivePreset: 'Aktive Vorlage:',
    settingsSectionTime: 'Zeit (Minuten)',
    settingsLabelPomodoro: 'Pomodoro',
    settingsLabelShortBreak: 'Kurze Pause',
    settingsLabelLongBreak: 'Lange Pause',
    settingsLabelIntervals: 'Intervalle',
    settingsSectionSignal: 'Signal-Ton',
    settingsLabelSignalVolume: 'Lautstärke',
    settingsSectionFeatures: 'Funktionen',
    settingsLabelCountdown: '10s Countdown aktivieren',
    settingsLabelAutoStart: 'Pomodoros automatisch starten',
    settingsLabelSkipControls: 'Überspringen / Pause-beenden-Steuerung aktivieren',
    settingsLabelExtensionControls: 'Schnelle Zeitverlängerung aktivieren',
    settingsLabelShowShortcutHints: 'Tastaturkürzel anzeigen',
    settingsBtnApply: 'Übernehmen',
    settingsBtnClose: 'Schließen',
    settingsBtnResetDefaults: 'Auf Standardwerte zurücksetzen',
    settingsBtnTestSound: 'Testton abspielen',


    // ── Preset descriptions (keyed by preset id) ─────────────────────────────
    presetDescriptions: {
      classic: '25 / 5 · 4 Runden',
      'deep-work': '50 / 10 · 3 Runden',
      sprint: '15 / 3 · 4 Runden',
      study: '45 / 15 · 3 Runden',
      custom: 'Eigene Zeiten',
    },

    // ── Theme descriptions (keyed by theme id) ────────────────────────────────
    themeDescriptions: {
      aurora: 'Kühle Himmelblautöne mit ruhigen Mintakzenten',
      ember: 'Warme Sandstein- und Glutrot-Töne für eine wärmere Arbeitsatmosphäre',
      midnight: 'Dunkle Schiefertöne mit leuchtenden Cyanakzenten',
      sage: 'Gedämpfte Eukalyptusgrün-Töne mit klaren steinfarbenen Neutrals',
      dusk: 'Sanfte Violett- und Abendtöne mit helleren Orchideenakzenten',
      graphite: 'Präzise Anthrazitflächen mit klaren elektrisch-blauen Hinweisen',
    },

    // ── Home screen ──────────────────────────────────────────────────────────
    homeAriaLabel: 'Informationen zu Pomodoro Flow',
    homeEyebrow: 'Fokus-Timer für tiefes Arbeiten',
    homeHero: 'Pomodoro Flow hilft dir, in bewussten Intervallen zu arbeiten statt abzudriften.',
    homeLead:
      'Nutze den Timer für Lernsessions, Schreiben, Programmieren, Verwaltungsaufgaben oder alles, das von einem klaren Rhythmus profitiert. Die Seite ist bewusst einfach gehalten: Timer starten, Einstellungen anpassen, wieder an die Arbeit.',
    homeHighlights: [
      'Kostenloser browserbasierter Pomodoro-Timer – ohne Account.',
      'Anpassbare Zeiten für Arbeit, Kurzpause, Langpause, Countdown und Intervalle.',
      'Eingebaute Vorlagen helfen dir, schnell loszulegen ohne lange Konfiguration.',
    ],
    homeFeatureIntroTitle: 'Wobei Pomodoro Flow konkret hilft',
    homeFeatureIntroLead:
      'Der Timer bleibt bewusst schlank, deckt aber die praktischen Bausteine ab, mit denen aus einer vagen Aufgabenliste ein verlässlicher Arbeitsrhythmus wird.',
    homeWorkflowLabel: 'Praxisbezug:',
    homeFeatureCards: [
      {
        title: 'Arbeitsblöcke an die Aufgabe anpassen',
        body: 'Arbeitszeit, Kurzpause, Langpause und Intervallanzahl lassen sich so einstellen, dass der Timer zur Aufgabe passt statt jede Session in dasselbe Schema zu pressen.',
        points: [
          'Kürzere Runden eignen sich für Admin, E-Mails oder Lernwiederholungen.',
          'Längere Fokusblöcke passen besser zu Schreiben, Programmieren oder Prüfungsvorbereitung.',
        ],
        workflow: 'Studierende können für Lernphasen längere Vorlagen nutzen, während Wissensarbeit eher kurze Zyklen für Inbox, Orga oder Nachbereitung braucht.',
      },
      {
        title: 'Im Fluss bleiben ohne an der Uhr herumzuspielen',
        body: 'Start, Pause, Reset, Countdown und optionaler Autostart helfen dabei, schnell loszulegen und den Ablauf stabil zu halten.',
        points: [
          'Der Countdown schafft einen kurzen, klaren Anlauf in die Fokusphase.',
          'Pause und Reset bleiben verfügbar, wenn der Alltag den Plan kurz unterbricht.',
        ],
        workflow: 'Praktisch, wenn man einen sauberen Start will, aber trotzdem mit Anrufen, Rückfragen oder Kontextwechseln im Tagesverlauf rechnen muss.',
      },
      {
        title: 'Einen verlässlichen Rhythmus aufbauen statt blind durchzuziehen',
        body: 'Kurze und lange Pausen sind Teil des Systems, damit Erholung nicht erst dann einfällt, wenn die Konzentration schon verbrannt ist.',
        points: [
          'Der Timer führt automatisch durch Arbeits- und Erholungsphasen.',
          'Die Langpause verhindert endloses Durchdrücken ohne echte Regeneration.',
        ],
        workflow: 'Hilfreich für Deep-Work-Sessions, in denen Konstanz wichtiger ist als stumpfes Dauerschieben über mehrere Runden hinweg.',
      },
      {
        title: 'Die Oberfläche an die eigene Fokusweise anpassen',
        body: 'Vorlagen, Designs, Shortcut-Hinweise, manuelle Steuerung und fokussierende Optionen helfen dabei, den Timer an unterschiedliche Routinen anzupassen, ohne die App aufzublähen.',
        points: [
          'Vorlagen senken die Einstiegshürde, wenn man einfach anfangen will.',
          'Tastatur- und Fokusfunktionen ermöglichen schnellere, reibungsärmere Steuerung.',
        ],
        workflow: 'Wer regelmäßig damit arbeitet, kann eine feste Routine behalten und den Timer trotzdem an Aufgabe oder Energielevel anpassen.',
      },
    ],
    homeHowTitle: 'Wie der Timer funktioniert',
    homeHowSteps: [
      'Bevorzugte Arbeits- und Pausenzeiten in den Einstellungen festlegen.',
      'Timer starten und auf eine Aufgabe konzentrieren bis die Arbeitsphase endet.',
      'Dem Rhythmus aus kurzen und langen Pausen folgen, um nachhaltig zu arbeiten.',
      'Optional Countdown und Autostart aktivieren, um die Routine anzupassen.',
    ],
    homePomodoroTitle: 'Was die Pomodoro-Technik ist und warum sie funktioniert',
    homePomodoroLead:
      'Die Pomodoro-Technik ist eine einfache Fokusmethode: Du arbeitest für einen klar begrenzten Zeitraum, machst danach eine kurze Pause und wiederholst das Ganze. Nach mehreren Runden folgt eine längere Pause, damit der Rhythmus tragfähig bleibt statt in stumpfes Durchziehen umzuschlagen.',
    homePomodoroHowItWorksTitle: 'So funktioniert sie in der Praxis',
    homePomodoroHowItWorksSteps: [
      'Eine konkrete Aufgabe wählen und für ein fokussiertes Intervall daran arbeiten, klassisch etwa 25 Minuten.',
      'Wenn der Timer endet, bewusst eine kurze Pause machen statt direkt in die nächste Sache zu stolpern.',
      'Den Zyklus einige Male wiederholen und danach eine längere Pause nutzen, um vor der nächsten Runde sauber zurückzusetzen.',
    ],
    homePomodoroWhyItHelpsTitle: 'Warum das beim Fokussieren hilft',
    homePomodoroBenefits: [
      'Kleinere Arbeitseinheiten senken die Hürde anzufangen, besonders wenn eine Aufgabe diffus oder überfordernd wirkt.',
      'Klare Zeitgrenzen bremsen Prokrastination und abschweifende Aufmerksamkeit, weil es ein definiertes Ende gibt.',
      'Geplante Pausen helfen dabei, Energie und geistige Frische zu halten, statt erst zu spät zu merken, dass die Konzentration verbrannt ist.',
      'Ein verlässlicher Arbeits-Pausen-Rhythmus macht Beständigkeit leichter, sodass Fortschritt weniger von Stimmung oder perfekter Motivation abhängt.',
    ],
    homeFaqTitle: 'Häufig gestellte Fragen',
    homeFaq: [
      {
        question: 'Welche Daten speichert die Seite?',
        answer:
          'Nur Timer-Einstellungen wie Dauern und Intervallanzahl werden lokal im Browser gespeichert. Details sind auf der Datenschutzseite dokumentiert.',
      },
      {
        question: 'Kann ich die Seite ohne Marketing-Cookies nutzen?',
        answer:
          'Ja. Der Timer funktioniert vollständig ohne Marketing-Zustimmung. Ohne Einwilligung liefert Google automatisch nicht-personalisierte Werbung – ohne Werbe-Cookies oder Tracking. Zustimmung kann jederzeit über die Datenschutzseite verwaltet oder widerrufen werden.',
      },
      {
        question: 'Kann ich den Timer an meine eigene Routine anpassen?',
        answer:
          'Ja. Du kannst eine Vorlage als Ausgangspunkt nehmen oder Arbeitszeit, Pausen, Countdown und Intervalle in den Einstellungen selbst einstellen.',
      },
    ],
  },
};

export default translations;

/** Convenience hook — returns the translation object for the current lang */
export function getT(lang) {
  return translations[lang] || translations.en;
}
